import { useCallback, useEffect, useMemo } from "react";
import { ApplicationFlowState } from "src/apps/AppFlow/screens/root/Layout";
import { LoanAmountDisclosure } from "src/components";
import { Page } from "src/components/FlowLayout";
import { amountRequestedMaximum, defaultAmountRequested } from "src/formatters";
import { useCurrentRate, useQueryParams } from "src/hooks";
import { PageStateConverter } from "src/hooks/usePageState";
import { Logger } from "src/log";

import { engine, enums, formatters, pick, underwritingConfig, utilities } from "@fraction/shared";

import _ from "lodash";
import {
  Applicants,
  CreditScore,
  HomeLocation,
  HomeValue,
  LoanAmountRequested,
  PrimaryPropertyMortgages,
  PropertyInfo,
  UseOfFunds,
} from "./pages";

const SLIDER_STEP = 500000;

const LOGGER = new Logger("ApplicationFlow.Layout.sections.PropertyAndLoanInformation");

export type PropertyAndLoanInformationArgs =
  | "amountRequested"
  | "applicants"
  | "propertyLocation"
  | "propertyValue"
  | "primaryPropertyMortgages"
  | "propertyType"
  | "useOfFunds"
  | "creditScoreGuess";

export type PropertyAndLoanInformationSectionArguments = PageStateConverter<
  ApplicationFlowState,
  PropertyAndLoanInformationArgs
>;

const PropertyAndLoanInformationPages = ({
  amountRequested,
  applicants,
  propertyLocation,
  propertyValue,
  primaryPropertyMortgages,
  propertyType,
  useOfFunds,
  creditScoreGuess,
}: PropertyAndLoanInformationSectionArguments): Page[] => {
  const { allowAnyRegion } = useQueryParams();

  const { rate, isFetched } = useCurrentRate(propertyLocation.value?.administrativeArea, {
    logger: LOGGER,
    allowAnyRegion: allowAnyRegion === "true",
  });

  useEffect(() => {
    if (!rate && isFetched) {
      LOGGER.warn("Estimate page is accessed without correct rates");
    }
  }, [rate, isFetched]);

  const existingMortgageAmount =
    primaryPropertyMortgages.value?.reduce(
      (sum, mortgage) => sum + formatters.number.getCents(mortgage?.mortgageBalanceAndLender?.amount || 0),
      0
    ) || 0;

  const parsedHomeValue = propertyValue.value ? formatters.number.getCents(propertyValue.value) : 0;

  // Ideally, we will boot people out of the flow if they have insufficient LTV.
  // However, that would require us to capture this information and send an adverse action notice.
  // So in the meantime, we just don't show our fancy "you need to pay off your mortgage"
  // if their LTV is too high
  const skipShowingExistingMortgageAmount =
    engine.subprocessors.ltvRatioValidationSubProcessor({
      homeValue: parsedHomeValue,
      existingMortgageAmount,
      distinguishUnfulfillable: true,
      maximumLTV: rate?.maximumLTV,
    }).status === enums.EngineRuleStatus.FAILED;
  const shownExistingMortgageAmount = skipShowingExistingMortgageAmount
    ? undefined
    : existingMortgageAmount && utilities.number.roundToTheNearest(existingMortgageAmount, 100000);

  const shownAmountRequested =
    amountRequested.value ||
    shownExistingMortgageAmount ||
    defaultAmountRequested(parsedHomeValue, SLIDER_STEP);
  const propertyAddress = propertyLocation?.value
    ? formatters.property.shortFormattedAddress(propertyLocation?.value)
    : undefined;

  const handleNextAmountRequested = useCallback(() => {
    // Pass the amount requested here in case there was nothing set before, as the state won't update
    // in time for this to be fired correctly otherwise
    amountRequested.onNext?.(shownAmountRequested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountRequested.onNext, shownAmountRequested]);

  const maximumLTV = useMemo(() => {
    if (!isFetched) {
      return underwritingConfig.MAX_ALLOWED_LTV;
    }
    if (propertyLocation.value?.administrativeArea) {
      if (rate?.maximumLTV) {
        return rate.maximumLTV;
      }
      LOGGER.log(
        `No maximum LTV found, using fallback value. Location: ${propertyLocation.value?.administrativeArea}`
      );
    }
    return underwritingConfig.MAX_ALLOWED_LTV;
  }, [propertyLocation.value?.administrativeArea, rate?.maximumLTV, isFetched]);

  return [
    {
      name: propertyLocation.name,
      component: (
        <HomeLocation title="What's the address of the property?" showConsent {...propertyLocation} />
      ),
    },
    {
      name: propertyValue.name,
      component: <HomeValue propertyAddress={propertyAddress} {...propertyValue} />,
    },
    {
      name: primaryPropertyMortgages.name,
      component: (
        <PrimaryPropertyMortgages
          rate={rate || undefined}
          propertyValue={
            propertyValue?.value !== undefined ? formatters.number.getCents(propertyValue?.value) : undefined
          }
          propertyAddress={propertyAddress}
          {...primaryPropertyMortgages}
        />
      ),
      pageNeedsToBeFilled: false,
    },
    {
      name: amountRequested.name,
      component: (
        <LoanAmountRequested
          sliderStep={SLIDER_STEP}
          sliderMin={5000000}
          maximumLTV={maximumLTV}
          sliderMax={amountRequestedMaximum(parsedHomeValue, maximumLTV)}
          {...amountRequested}
          value={shownAmountRequested}
          homeValue={parsedHomeValue}
          onNext={handleNextAmountRequested}
          existingMortgageAmount={shownExistingMortgageAmount}
        >
          <LoanAmountDisclosure />
        </LoanAmountRequested>
      ),
    },
    {
      name: applicants.name,
      component: (
        <Applicants
          {...applicants}
          value={_.sortBy(
            applicants?.value?.map((value) => pick(value, ["name", "email", "id", "priority"])),
            "priority"
          )}
        />
      ),
    },
    {
      name: propertyType.name,
      component: <PropertyInfo propertyAddress={propertyAddress} {...propertyType} />,
    },
    {
      name: useOfFunds.name,
      component: <UseOfFunds {...useOfFunds} />,
    },
    {
      name: creditScoreGuess.name,
      component: <CreditScore {...creditScoreGuess} />,
    },
  ].filter((p): p is Page => Boolean(p));
};

export default PropertyAndLoanInformationPages;
